import React, { useState } from "react";
import { Box, Typography, Button, Chip, SvgIcon } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import { FileDownloadOutlined, Star } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DeleteServicePopup from "../../cart-page/components/DeleteServicePopup";
import { ReactComponent as NoImgIcon } from "../../../assets/icons/no-img-icon.svg";
import {
  cancelUserOrder,
  userInvoiceDownload,
} from "../../../api/order-summary.api";
import { AlertComponent } from "../../../common/components";
import "./OrderHistoryCard.scss";

const statusColorMap = {
  confirmed: { bg: "#dcfce7", text: "#008236" },
  paid: { bg: "#dcfce7", text: "#008236" },
  pending: { bg: "#FEF3C6", text: "#BB4D00" },
  cancelled: { bg: "#FFE2E2", text: "#C10007" },
  completed: { bg: "#EEF2FF", text: "#6366F1" },
};

const isAllowInvoiceDownload = (value: unknown): boolean =>
  value === true || value == 1 || String(value).toLowerCase().trim() === "true";

interface OrderHistoryCardProps {
  order: any;
  onOrderCancelled?: (message?: string, type?: string) => void;
}

const OrderHistoryCard: React.FC<OrderHistoryCardProps> = ({
  order,
  onOrderCancelled,
}) => {
  const { orderId, orderDate, totalAmount, rating } = order ?? {};
  const status = order?.status ?? order?.orderStatus;
  const canShowUserOrderInvoice =
    isAllowInvoiceDownload(order?.allowInvoiceDownload) ||
    ["paid", "confirmed", "completed"].includes(
      status?.toLowerCase(),
    );

  const partner = order?.providers?.[0] || order?.partners?.[0];
  const isPlanner = !!partner?.plannerId;

  const avgRating = partner?.averageRating || partner?.partnerAvgRating || partner?.plannerAvgRating;

  const normalizedStatus = status?.toLowerCase() as keyof typeof statusColorMap;
  const { bg, text } = statusColorMap[normalizedStatus] || {
    bg: "#E0E0E0",
    text: "#000000",
  };

  const navigate = useNavigate();

  const [openCancelPopup, setOpenCancelPopup] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const userDetails = order?.userDetails;
  const servicesData = partner?.services || partner?.packages;
  const services = servicesData?.map((item: any) => item.serviceName || item.packageName) || [];

  const isImageUrl = (url?: string) => {
    if (!url) return false;
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".svg",
      ".bmp",
    ];
    const lowerUrl = url.split("?")[0].toLowerCase();
    return imageExtensions.some((ext) => lowerUrl.endsWith(ext));
  };

  const images =
    (isPlanner ? partner?.plannerImages : partner?.partnerImages) || [];
  const displayImage =
    (isImageUrl(partner?.image) ? partner?.image : null) ||
    images.find((img: any) => img.mediaType === "image")?.url;

  const name = isPlanner ? partner?.plannerName : (partner?.partnerName || partner?.name);
  const city = partner?.city || partner?.partnerFullAddress?.city || partner?.plannerLocation?.split(",")?.[0];
  const state = partner?.state || partner?.partnerFullAddress?.state || partner?.plannerLocation?.split(",")?.[1]?.trim();

  const handleViewDetails = () => {
    navigate(`${orderId}`);
  };

  const handleInvoice = async () => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await userInvoiceDownload(orderId, userTimezone);

      // Create a blob from the response data
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${orderId}.pdf`); // or the appropriate file extension
      document.body.appendChild(link);
      link.click();

      // Clean up
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);

      setAlertOpen(true);
      setAlertType("success");
      setAlertMessage("Your invoice has been downloaded successfully.");
    } catch (error: any) {
      setAlertOpen(true);
      setAlertType("error");
      setAlertMessage(`Invoice download failed: ${error.message}`);
      console.log("ERROR DOWNLOAD::", error);
    }
  };
  const statusLabelMap: Record<string, string> = {
    confirmed: "Confirmed",
    paid: "Confirmed",
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  const handleCancelOrder = () => {
    setOpenCancelPopup(true);
  };

  const handleConfirmCancel = async () => {
    setOpenCancelPopup(false);
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await cancelUserOrder(orderId, userTimezone);

      if (response.data.success) {
        // Instead of showing local alert:
        if (onOrderCancelled) {
          onOrderCancelled("Your order has been cancelled successfully.");
        }
      }
    } catch (error: any) {
      if (onOrderCancelled) {
        onOrderCancelled(
          `Your order can't be cancelled due to ${error.message}`,
          "error",
        );
      }
      console.error("ERROR CANCELING ORDER::", error);
    }
  };

  return (
    <Box className="order-history-card">
      {/* Top Row */}
      <Box className="order-history-card__top">
        <Typography className="order-history-card__order-id">
          {[userDetails?.first_name, userDetails?.last_name].filter(v => v && v.trim()).join(" ") || "-"} - Order #{orderId}
        </Typography>

        <Typography className="order-history-card__date">
          {new Date(orderDate).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </Typography>
      </Box>

      {/* Content */}
      <Box className="order-history-card__content">
        {/* Left Section */}
        <Box className="order-history-card__left">
          <Box
            className="order-history-card__image-wrapper"
            sx={{ position: "relative" }}
          >
            {displayImage ? (
              <>
                <img
                  src={displayImage}
                  alt="service"
                  className="order-history-card__image"
                />
              </>
            ) : (
              <Box className="order-history-card__image-box">
                <SvgIcon
                  component={NoImgIcon}
                  style={{
                    height: "48px",
                    width: "48px",
                  }}
                  inheritViewBox
                />
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#777",
                    display: "flex",
                  }}
                >
                  No Image Available
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                top: 4,
                left: 4,
                position: "absolute",
              }}
            >
              {avgRating && (
                <Box className="order-history-card__rating-box">
                  <Star sx={{ color: "#E9AE12" }} fontSize="small" />{" "}
                  {avgRating}
                </Box>
              )}
            </Box>
          </Box>

          <Box className="order-history-card__details">
            <Chip
              label={statusLabelMap[status?.toLowerCase()] || status}
              size="small"
              className="order-history-card__status"
              sx={{
                backgroundColor: bg,
                color: text,
                fontWeight: 500,
                height: 24,
              }}
            />

            <Box className="order-history-card__provider">
              <Typography className="order-history-card__provider-name">
                {name ?? "-"}
              </Typography>

              <FiberManualRecordIcon className="order-history-card__dot" />

              <Box className="order-history-card__location">
                <Typography>
                  {city ?? "-"}, {state ?? "-"}
                </Typography>
              </Box>
            </Box>

            <Typography className="order-history-card__service">
              {servicesData?.join(", ")}
            </Typography>
          </Box>
        </Box>

        {/* Right Section */}
        <Box className="order-history-card__right">
          <Typography className="order-history-card__price">
            ${totalAmount}
          </Typography>

          <Box className="order-history-card__actions">
            {["pending", "confirmed"].includes(status?.toLowerCase()) && (
              <Button
                variant="outlined"
                className="order-history-card__cancel-btn"
                onClick={handleCancelOrder}
              >
                Cancel Order
              </Button>
            )}

            <Button
              variant="outlined"
              className="order-history-card__invoice-btn"
              onClick={handleViewDetails}
            >
              <RemoveRedEyeOutlined
                fontSize="small"
                sx={{
                  mr: 1,
                }}
              />{" "}
              View Details
            </Button>

            {canShowUserOrderInvoice && (
              <Button
                variant="outlined"
                className="order-history-card__invoice-btn"
                onClick={handleInvoice}
              >
                <FileDownloadOutlined
                  fontSize="small"
                  sx={{
                    mr: 1,
                  }}
                />{" "}
                Invoice
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <DeleteServicePopup
        open={openCancelPopup}
        message="Are you sure you want to cancel this order?"
        onClose={() => setOpenCancelPopup(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Order"
      />
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default OrderHistoryCard;
